// Two-state client-side diff. Runs the viewer-core pipeline against
// two in-memory VFS bundles (the "before" and "after" the user
// uploaded), then runs @archlang/engine's diffModels over the two
// resolved models. No server, no git.
//
// Git-ref diff lives in @archlang/studio-web/diff/useGitDiff.ts and
// will return when Studio comes online.

import { useEffect, useState } from "react";
import { diffModels, type ChangeSet, type ResolvedModel } from "@archlang/engine";
import {
  callWireEdges,
  loadViewer,
  serializeEdges,
  serializeModel,
  type Vfs,
} from "@archlang/viewer-core";
import type { DependencyEdge, Module } from "@archlang/scene/arch-model";

export interface DiffState {
  changeSet: ChangeSet | null;
  before: readonly Module[] | null;
  after: readonly Module[] | null;
  beforeEdges: readonly DependencyEdge[];
  afterEdges: readonly DependencyEdge[];
  /** Per-side raw resolved models — the process diff (plan P4) builds walker
   *  + scaffold contexts from these; retained, not recomputed per pane. */
  beforeRaw: ResolvedModel | null;
  afterRaw: ResolvedModel | null;
  loading: boolean;
  error: string | null;
}

const EMPTY: DiffState = {
  changeSet: null,
  before: null,
  after: null,
  beforeEdges: [],
  afterEdges: [],
  beforeRaw: null,
  afterRaw: null,
  loading: false,
  error: null,
};

export function useDiff(beforeVfs: Vfs | null, afterVfs: Vfs | null): DiffState {
  const [state, set] = useState<DiffState>({ ...EMPTY, loading: true });

  useEffect(() => {
    if (!beforeVfs || !afterVfs) {
      set({ ...EMPTY, loading: false });
      return;
    }
    let cancelled = false;
    set((s) => ({ ...s, loading: true, error: null }));
    Promise.all([loadViewer({ sources: beforeVfs }), loadViewer({ sources: afterVfs })])
      .then(([a, b]) => {
        if (cancelled) return;
        // diffModels operates on raw (pre-cascade) resolved models so
        // explicit overrides aren't mistaken for inherited cascades.
        const changeSet = diffModels(a.rawModel, b.rawModel);
        set({
          before: serializeModel(a.model).modules as unknown as readonly Module[],
          after: serializeModel(b.model).modules as unknown as readonly Module[],
          // Board diff renders module→interface edges; `do` edges are filtered
          // at the wire boundary (design P0.1) so the diff board stays intact.
          beforeEdges: callWireEdges(serializeEdges(a.graph)) as DependencyEdge[],
          afterEdges: callWireEdges(serializeEdges(b.graph)) as DependencyEdge[],
          beforeRaw: a.rawModel as ResolvedModel,
          afterRaw: b.rawModel as ResolvedModel,
          changeSet,
          loading: false,
          error: null,
        });
      })
      .catch((err) => {
        if (!cancelled) {
          set((s) => ({ ...s, loading: false, error: String(err) }));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [beforeVfs, afterVfs]);

  return state;
}
